import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { InlineSpinner } from "@/components/common/Loading";
import { Plus } from "lucide-react";
import { toast } from "sonner";

// Generic manager reused by Person Classifications and Candidate Types
export function ConfigManager({ title, description, endpoint, testPrefix }) {
  const { currentAccount } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setItems(data.items); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [endpoint, currentAccount]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (item) => {
    try { await api.patch(`${endpoint}/${item.id}`, { active: !item.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    if (!form.label) return setError("Label is required");
    setError(""); setSaving(true);
    try { await api.post(endpoint, form); toast.success("Created"); setOpen(false); setForm({ label: "", description: "" }); load(); }
    catch (e) { setError(apiError(e)); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: "label", header: "Label", render: (r) => (
      <span className="font-medium text-slate-800">{r.label}
        {r.is_system && <span className="ml-2 text-xs text-slate-400">(system)</span>}</span>) },
    { key: "key", header: "Key", render: (r) => <span className="font-mono text-xs text-slate-500">{r.key}</span> },
    { key: "description", header: "Description", render: (r) => r.description || <span className="text-slate-300">—</span> },
    { key: "active", header: "Active", className: "text-right", render: (r) => (
      <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`${testPrefix}-toggle-${r.key}`} />) },
  ];

  return (
    <div>
      <PageHeader title={title} description={description}
        actions={<Button className="bg-[#006090] hover:bg-[#004E75]" data-testid={`${testPrefix}-add`} onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" /> Add</Button>} />
      <DataTable columns={columns} rows={items} loading={loading} testId={`${testPrefix}-table`} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid={`${testPrefix}-dialog`}>
          <DialogHeader><DialogTitle>Add {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <div><Label className="text-xs font-semibold text-slate-700">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" data-testid={`${testPrefix}-label`} /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid={`${testPrefix}-submit`} className="bg-[#006090] hover:bg-[#004E75]">{saving ? <InlineSpinner /> : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PersonClassifications() {
  return <ConfigManager title="Person Classifications" endpoint="/classification-types" testPrefix="classification"
    description="A person can hold multiple classifications. Add your own — history is always preserved." />;
}

export function CandidateTypes() {
  return <ConfigManager title="Candidate Types" endpoint="/candidate-types" testPrefix="candidate-type"
    description="Candidate Type belongs to a recruiting opportunity and is configurable per account." />;
}
